/**
 * Shared Booking Detail component
 * Used by both lawyer/bookings/$accessId.tsx and tools/bookings/$accessId.tsx
 */
import { useState, useEffect, useCallback, useRef } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { motion, AnimatePresence } from 'framer-motion'
import {
  ArrowLeft,
  RefreshCw,
  Activity,
  Clock,
  AlertCircle,
  Check,
  Settings,
  History,
  GitBranch,
  CheckCircle2,
  Sparkles,
  Archive,
} from 'lucide-react'
import { lawyerBookingsAPI } from '@/utils/api'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Switch } from '@/components/ui/switch'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Input } from '@/components/ui/input'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { toast } from 'sonner'
import { cn } from '@/lib/utils'
import { PolentaViewer } from '@/components/polenta'
import type { BookingData, Dictionaries, Version, Delta } from '@/components/polenta/utils/types'
import { parseChanges } from '@/components/polenta/utils/changeParser'

interface BookingResponse {
  pnr: string
  lastname: string
  booking_id: number
  is_monitoring: boolean
  monitoring_frequency: string | null
  monitoring_until: string | null
  monitoring_active: boolean
  check_count: number
  change_count: number
  last_checked_at: string | null
  current_state: {
    data?: BookingData
    dictionaries?: Dictionaries
    errors?: Array<{ code: string; title: string; detail: string }>
  } | null
  versions: Version[]
  deltas: Delta[]
  is_archived: boolean
  archived_at: string | null
}

// Animation configs
const springConfig = {
  bouncy: { type: 'spring' as const, stiffness: 400, damping: 20 },
}

type RefreshStatus = 'idle' | 'refreshing' | 'success_no_changes' | 'success_with_changes'

// Refresh overlay component
function RefreshOverlay({
  status,
  onDismiss
}: {
  status: RefreshStatus
  onDismiss: () => void
}) {
  useEffect(() => {
    if (status === 'success_no_changes' || status === 'success_with_changes') {
      const timer = setTimeout(onDismiss, 3000)
      return () => clearTimeout(timer)
    }
  }, [status, onDismiss])

  return (
    <AnimatePresence>
      {status !== 'idle' && (
        <motion.div
          key="refresh-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/20 backdrop-blur-sm"
          onClick={status !== 'refreshing' ? onDismiss : undefined}
        >
        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          transition={springConfig.bouncy}
          className="bg-white rounded-2xl shadow-2xl p-8 max-w-sm mx-4 text-center"
          onClick={(e) => e.stopPropagation()}
        >
          {status === 'refreshing' && (
            <>
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 1.5, repeat: Infinity, ease: 'linear' }}
                className="mx-auto mb-4 w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center"
              >
                <RefreshCw className="h-8 w-8 text-blue-600" />
              </motion.div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">
                Buchung wird aktualisiert
              </h3>
              <p className="text-sm text-slate-500">
                Aktuelle Daten werden von Lufthansa abgerufen...
              </p>
            </>
          )}

          {status === 'success_no_changes' && (
            <>
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={springConfig.bouncy}
                className="mx-auto mb-4 w-16 h-16 rounded-full bg-green-100 flex items-center justify-center"
              >
                <CheckCircle2 className="h-8 w-8 text-green-600" />
              </motion.div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">
                Keine Änderungen
              </h3>
              <p className="text-sm text-slate-500">
                Die Buchung ist unverändert. Dies ist die aktuelle Version.
              </p>
            </>
          )}

          {status === 'success_with_changes' && (
            <>
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={springConfig.bouncy}
                className="mx-auto mb-4 w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center"
              >
                <Sparkles className="h-8 w-8 text-amber-600" />
              </motion.div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">
                Neue Änderungen erkannt!
              </h3>
              <p className="text-sm text-slate-500">
                Die Buchung wurde aktualisiert. Überprüfen Sie die Änderungen im Verlauf.
              </p>
            </>
          )}
        </motion.div>
      </motion.div>
      )}
    </AnimatePresence>
  )
}

// Helper to format relative time in German
function formatRelativeTime(date: Date): string {
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)

  if (diffMins < 1) return 'gerade eben'
  if (diffMins < 60) return `vor ${diffMins} Min.`
  if (diffHours < 24) return `vor ${diffHours} Std.`
  if (diffDays < 7) return `vor ${diffDays} ${diffDays === 1 ? 'Tag' : 'Tagen'}`

  return date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

export interface BookingDetailProps {
  /** The booking access ID from the URL */
  accessId: string
  /** Path to navigate back to (e.g., '/lawyer/bookings' or '/tools/bookings') */
  backRoute: string
}

export function BookingDetail({ accessId, backRoute }: BookingDetailProps) {
  const navigate = useNavigate()
  const [booking, setBooking] = useState<BookingResponse | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [selectedVersion, setSelectedVersion] = useState('current')

  // For version-specific data fetching
  const [versionData, setVersionData] = useState<{
    data?: BookingData
    dictionaries?: Dictionaries
  } | null>(null)
  const [isLoadingVersion, setIsLoadingVersion] = useState(false)

  // Check if booking data has an error state (e.g., ORDER_NOT_FOUND stored before archiving feature)
  const hasDataError = booking?.current_state?.errors && !booking?.current_state?.data
  const isBookingArchived = booking?.is_archived || false

  // Monitoring state
  const [isMonitoring, setIsMonitoring] = useState(false)
  const [monitoringFrequency, setMonitoringFrequency] = useState('daily')
  const [monitoringUntil, setMonitoringUntil] = useState('')
  const [isSavingMonitoring, setIsSavingMonitoring] = useState(false)

  // Refresh status state
  const [refreshStatus, setRefreshStatus] = useState<RefreshStatus>('idle')
  const previousChangeCountRef = useRef<number | null>(null)
  const refreshPollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Polling state for when booking data is being fetched
  const [isPolling, setIsPolling] = useState(false)
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const pollStartRef = useRef<number | null>(null)
  const POLL_INTERVAL_MS = 3000 // Poll every 3 seconds
  const POLL_TIMEOUT_MS = 60000 // Stop polling after 60 seconds

  // Helper to stop polling
  const stopPolling = useCallback(() => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current)
      pollIntervalRef.current = null
    }
    pollStartRef.current = null
    setIsPolling(false)
  }, [])

  const loadBooking = useCallback(async () => {
    try {
      const response = await lawyerBookingsAPI.getBookingDetail(accessId)
      const data = response.booking as BookingResponse

      setBooking(data)
      setVersionData(data.current_state)
      setIsMonitoring(data.is_monitoring)
      setMonitoringFrequency(data.monitoring_frequency || 'daily')
      setMonitoringUntil(
        data.monitoring_until
          ? new Date(data.monitoring_until).toISOString().split('T')[0]
          : ''
      )
      setError(null)

      // Check if we have booking data
      if (data.current_state) {
        // Data is available - stop polling and mark as read
        stopPolling()
        await lawyerBookingsAPI.markAsRead(accessId)
      } else {
        // No data yet - start or continue polling
        const now = Date.now()

        if (!pollStartRef.current) {
          pollStartRef.current = now
        }

        // Check if we've exceeded the timeout
        if (now - pollStartRef.current > POLL_TIMEOUT_MS) {
          stopPolling()
          toast.error('Zeitüberschreitung bei Abfrage', {
            description: 'Buchungsdaten konnten nicht abgerufen werden. Bitte aktualisieren.',
          })
        } else if (!pollIntervalRef.current) {
          // Start polling
          setIsPolling(true)
          pollIntervalRef.current = setInterval(() => {
            loadBooking()
          }, POLL_INTERVAL_MS)
        }
      }
    } catch (err: any) {
      console.error('Failed to load booking:', err)
      stopPolling()
      if (err.response?.status === 404) {
        setError('Buchung nicht gefunden')
      } else {
        setError('Buchungsdetails konnten nicht geladen werden')
      }
    } finally {
      setIsLoading(false)
    }
  }, [accessId, stopPolling])

  // Load version-specific data when version changes
  const loadVersionData = useCallback(async (version: string) => {
    if (!booking) return

    if (version === 'current') {
      setVersionData(booking.current_state)
      return
    }

    setIsLoadingVersion(true)
    try {
      // Fetch version-specific data from the polenta API
      const response = await lawyerBookingsAPI.getBookingVersion(accessId, version)
      if (response.data && response.dictionaries) {
        setVersionData({
          data: response.data,
          dictionaries: response.dictionaries,
        })
      }
    } catch (err) {
      console.error('Failed to load version:', err)
      toast.error('Version konnte nicht geladen werden', {
        description: 'Historische Buchungsdaten konnten nicht geladen werden',
      })
      // Fall back to current state
      setVersionData(booking.current_state)
    } finally {
      setIsLoadingVersion(false)
    }
  }, [accessId, booking])

  useEffect(() => {
    loadBooking()
  }, [loadBooking])

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current)
      }
      if (refreshPollTimeoutRef.current) {
        clearTimeout(refreshPollTimeoutRef.current)
      }
    }
  }, [])

  useEffect(() => {
    if (booking) {
      loadVersionData(selectedVersion)
    }
  }, [selectedVersion, booking, loadVersionData])

  const handleRefresh = async () => {
    setIsRefreshing(true)
    setRefreshStatus('refreshing')

    // Store the current change count to detect new changes
    previousChangeCountRef.current = booking?.change_count ?? null

    try {
      await lawyerBookingsAPI.refreshBooking(accessId)

      // Poll for updates with change detection
      const pollForResult = async (attempts = 0, maxAttempts = 20) => {
        if (attempts >= maxAttempts) {
          setRefreshStatus('idle')
          toast.error('Zeitüberschreitung', {
            description: 'Aktualisierung dauert länger als erwartet. Bitte Seite manuell neu laden.',
          })
          return
        }

        try {
          const response = await lawyerBookingsAPI.getBookingDetail(accessId)
          const data = response.booking as BookingResponse

          // Check if the last_checked_at has been updated (meaning refresh completed)
          const newLastChecked = data.last_checked_at ? new Date(data.last_checked_at) : null
          const oldLastChecked = booking?.last_checked_at ? new Date(booking.last_checked_at) : null

          if (newLastChecked && (!oldLastChecked || newLastChecked > oldLastChecked)) {
            // Refresh completed - update state
            setBooking(data)
            setVersionData(data.current_state)

            // Detect if there were new changes
            const previousCount = previousChangeCountRef.current
            const newCount = data.change_count
            const hasNewChanges = previousCount !== null && newCount > previousCount

            if (hasNewChanges) {
              setRefreshStatus('success_with_changes')
            } else {
              setRefreshStatus('success_no_changes')
            }

            await lawyerBookingsAPI.markAsRead(accessId)
          } else {
            // Not yet updated, continue polling
            refreshPollTimeoutRef.current = setTimeout(() => pollForResult(attempts + 1, maxAttempts), 3000)
          }
        } catch (err) {
          console.error('Polling error:', err)
          refreshPollTimeoutRef.current = setTimeout(() => pollForResult(attempts + 1, maxAttempts), 3000)
        }
      }

      // Start polling after initial delay
      refreshPollTimeoutRef.current = setTimeout(() => pollForResult(), 2000)

    } catch (err: any) {
      setRefreshStatus('idle')
      toast.error('Aktualisierung fehlgeschlagen', {
        description: err.response?.data?.message || 'Bitte erneut versuchen',
      })
    } finally {
      setIsRefreshing(false)
    }
  }

  const handleSaveMonitoring = async () => {
    if (isMonitoring && !monitoringUntil) {
      toast.error('Enddatum erforderlich', {
        description: 'Bitte ein Enddatum für die Überwachung setzen',
      })
      return
    }

    setIsSavingMonitoring(true)
    try {
      await lawyerBookingsAPI.updateMonitoring(accessId, {
        is_monitoring: isMonitoring,
        monitoring_frequency: monitoringFrequency,
        monitoring_until: isMonitoring ? new Date(monitoringUntil).toISOString() : null,
      })
      toast.success('Einstellungen gespeichert', {
        description: isMonitoring ? 'Überwachung ist jetzt aktiv' : 'Überwachung deaktiviert',
      })
    } catch (err: any) {
      toast.error('Speichern fehlgeschlagen', {
        description: err.response?.data?.message || 'Bitte erneut versuchen',
      })
    } finally {
      setIsSavingMonitoring(false)
    }
  }

  const handleVersionChange = (version: string) => {
    setSelectedVersion(version)
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="flex flex-col items-center gap-4">
          <div className="h-10 w-10 border-4 border-slate-200 border-t-blue-500 rounded-full animate-spin" />
          <p className="text-slate-500">Buchung wird geladen...</p>
        </div>
      </div>
    )
  }

  if (error || !booking) {
    return (
      <div className="flex flex-col items-center justify-center h-screen">
        <AlertCircle className="h-12 w-12 text-red-400 mb-4" />
        <p className="text-slate-900 font-medium mb-4">{error || 'Unbekannter Fehler'}</p>
        <Button variant="outline" onClick={() => navigate({ to: backRoute })}>
          Zurück zu Buchungen
        </Button>
      </div>
    )
  }

  return (
    <div className="h-screen flex flex-col">
      {/* Header - Responsive layout */}
      <div className="shrink-0 border-b bg-white px-3 md:px-6 py-3 md:py-4">
        {/* Mobile: Stack elements vertically */}
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          {/* Left section: Back button + PNR info */}
          <div className="flex items-center gap-2 md:gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate({ to: backRoute })}
              className="gap-1.5 md:gap-2 px-2 md:px-3"
            >
              <ArrowLeft className="h-4 w-4" />
              <span className="hidden md:inline">Zurück</span>
            </Button>

            <div className="hidden md:block h-8 w-px bg-slate-200" />

            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 md:gap-3 flex-wrap">
                <h1 className="font-mono text-xl md:text-2xl font-bold text-slate-900">
                  {booking.pnr}
                </h1>
                <Badge variant="secondary" className="text-xs">{booking.lastname}</Badge>
                {isBookingArchived && (
                  <Badge className="bg-slate-100 text-slate-600 border-slate-200 text-xs">
                    <Archive className="h-3 w-3 mr-1" />
                    Archiviert
                  </Badge>
                )}
                {!isBookingArchived && booking.monitoring_active && (
                  <Badge className="bg-green-100 text-green-700 border-green-200 text-xs hidden md:inline-flex">
                    <Activity className="h-3 w-3 mr-1" />
                    Überwachung
                  </Badge>
                )}
              </div>
              <div className="text-xs md:text-sm text-slate-500 hidden md:flex md:items-center md:gap-3">
                <span>{booking.check_count} Prüfungen, {booking.change_count} Änderungen erkannt</span>
                {booking.last_checked_at && (
                  <>
                    <span className="text-slate-300">•</span>
                    <span className="flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      Zuletzt geprüft: {formatRelativeTime(new Date(booking.last_checked_at))}
                    </span>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Right section: Actions - horizontal scroll on mobile */}
          <div className="flex items-center gap-2 md:gap-3 overflow-x-auto pb-1 md:pb-0 -mx-3 px-3 md:mx-0 md:px-0">
            {/* Version Picker */}
            {(() => {
              // Deduplicate versions with same timestamp
              // Keep 'current' over 'base' when timestamps match
              const versions = booking.versions || []
              const uniqueVersions: typeof versions = []
              const seenTimestamps = new Set<string>()

              // Process in reverse to prioritize 'current' over numbered versions, and numbered over 'base'
              const priorityOrder = ['current', ...versions.filter(v => v.version !== 'base' && v.version !== 'current').map(v => v.version).reverse(), 'base']

              for (const priority of priorityOrder) {
                const v = versions.find(ver => ver.version === priority)
                if (v && !seenTimestamps.has(v.timestamp)) {
                  seenTimestamps.add(v.timestamp)
                  uniqueVersions.push(v)
                }
              }

              // Sort by timestamp descending (newest first)
              uniqueVersions.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())

              // Format date nicely: "7. Dez, 14:03"
              const formatVersionDate = (timestamp: string, short = false) => {
                const date = new Date(timestamp)
                const day = date.getDate()
                const month = date.toLocaleDateString('de-DE', { month: 'short' })
                const time = date.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })
                // Short format for mobile: "7. Dez" (no time)
                if (short) {
                  return `${day}. ${month}`
                }
                return `${day}. ${month}, ${time}`
              }

              // Get display label for current selection
              const selectedVersionData = uniqueVersions.find(v => v.version === selectedVersion) || uniqueVersions[0]
              const isCurrent = selectedVersion === 'current' || selectedVersionData?.version === 'current'

              // If only one version, just show a static badge
              if (uniqueVersions.length <= 1) {
                return (
                  <div className="flex items-center gap-1.5 md:gap-2 shrink-0">
                    <GitBranch className="h-4 w-4 text-slate-400 hidden md:block" />
                    <div className="flex items-center gap-1.5 md:gap-2 px-2 md:px-3 py-1.5 bg-white border border-slate-200 rounded-md text-sm">
                      {/* Mobile: short date, Desktop: full date */}
                      <span className="text-slate-700 font-medium md:hidden">
                        {selectedVersionData ? formatVersionDate(selectedVersionData.timestamp, true) : 'Aktuell'}
                      </span>
                      <span className="text-slate-700 font-medium hidden md:inline">
                        {selectedVersionData ? formatVersionDate(selectedVersionData.timestamp) : 'Aktuell'}
                      </span>
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700">
                        Aktuell
                      </span>
                    </div>
                  </div>
                )
              }

              return (
                <div className="flex items-center gap-1.5 md:gap-2 shrink-0">
                  <GitBranch className="h-4 w-4 text-slate-400 hidden md:block" />
                  <Select value={selectedVersion} onValueChange={handleVersionChange}>
                    <SelectTrigger className="w-auto min-w-[100px] md:min-w-[180px] bg-white">
                      <div className="flex items-center gap-1.5 md:gap-2">
                        {/* Mobile: short date, Desktop: full date */}
                        <span className="text-slate-700 md:hidden">
                          {selectedVersionData ? formatVersionDate(selectedVersionData.timestamp, true) : ''}
                        </span>
                        <span className="text-slate-700 hidden md:inline">
                          {selectedVersionData ? formatVersionDate(selectedVersionData.timestamp) : ''}
                        </span>
                        {isCurrent && (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700">
                            Aktuell
                          </span>
                        )}
                      </div>
                    </SelectTrigger>
                    <SelectContent>
                      {uniqueVersions.map((v) => {
                        const isCurrentVersion = v.version === 'current'
                        return (
                          <SelectItem key={v.version} value={v.version}>
                            <div className="flex items-center gap-2">
                              <span className="text-slate-700">
                                {formatVersionDate(v.timestamp)}
                              </span>
                              {isCurrentVersion && (
                                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700">
                                  Aktuell
                                </span>
                              )}
                            </div>
                          </SelectItem>
                        )
                      })}
                    </SelectContent>
                  </Select>
                </div>
              )
            })()}

            {/* Change History Popover */}
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" className="gap-1.5 md:gap-2 shrink-0">
                  <History className="h-4 w-4" />
                  <span className="hidden md:inline">Verlauf</span>
                  {booking.deltas && booking.deltas.length > 0 && (
                    <span className="bg-highlight text-highlight-text text-xs px-1.5 py-0.5 rounded-full">
                      {booking.deltas.length}
                    </span>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-80 p-0" align="end">
                <div className="p-3 border-b bg-slate-50">
                  <h4 className="font-semibold text-slate-900">Änderungsverlauf</h4>
                </div>
                <div className="max-h-80 overflow-y-auto">
                  {(booking.deltas || []).length === 0 ? (
                    <p className="text-sm text-slate-500 text-center py-8">
                      Noch keine Änderungen erkannt
                    </p>
                  ) : (
                    <div className="p-2 space-y-2">
                      {booking.deltas.map((delta) => {
                        const changeSummaries = parseChanges(delta.changes)
                        return (
                          <button
                            key={delta.id}
                            onClick={() => handleVersionChange(String(delta.sequence_number))}
                            className={cn(
                              'w-full text-left p-2.5 rounded-lg border transition-colors text-sm',
                              selectedVersion === String(delta.sequence_number)
                                ? 'bg-highlight-hover border-highlight'
                                : 'bg-white border-slate-200 hover:bg-slate-50'
                            )}
                          >
                            <div className="flex items-center gap-2 mb-2">
                              <span className="text-xs font-mono bg-slate-100 px-1.5 py-0.5 rounded">
                                #{delta.sequence_number}
                              </span>
                              <span className="text-xs text-slate-500">
                                {new Date(delta.applied_at).toLocaleString('de-DE', {
                                  day: '2-digit',
                                  month: '2-digit',
                                  year: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit',
                                })}
                              </span>
                            </div>
                            <div className="space-y-1">
                              {changeSummaries.length > 0 ? (
                                changeSummaries.slice(0, 4).map((summary) => {
                                  const IconComponent = summary.icon
                                  return (
                                    <div key={summary.category} className="flex items-center gap-1.5 text-xs text-slate-600">
                                      <IconComponent className="h-3 w-3 text-slate-400 shrink-0" />
                                      <span>{summary.description}</span>
                                    </div>
                                  )
                                })
                              ) : (
                                <span className="text-xs text-slate-400">Keine Details verfügbar</span>
                              )}
                              {changeSummaries.length > 4 && (
                                <span className="text-xs text-slate-400">
                                  +{changeSummaries.length - 4} weitere
                                </span>
                              )}
                            </div>
                          </button>
                        )
                      })}
                    </div>
                  )}
                </div>
              </PopoverContent>
            </Popover>

            {/* Monitoring Settings Popover */}
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" className="gap-1.5 md:gap-2 shrink-0">
                  <Settings className="h-4 w-4" />
                  <span className="hidden md:inline">Überwachen</span>
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-72" align="end">
                <div className="space-y-4">
                  <h4 className="font-semibold text-slate-900">Überwachungseinstellungen</h4>

                  <div className="flex items-center justify-between">
                    <Label>Überwachung aktivieren</Label>
                    <Switch
                      checked={isMonitoring}
                      onCheckedChange={setIsMonitoring}
                    />
                  </div>

                  {isMonitoring && (
                    <>
                      <div className="space-y-2">
                        <Label className="text-sm text-slate-600">Prüfintervall</Label>
                        <Select
                          value={monitoringFrequency}
                          onValueChange={setMonitoringFrequency}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="hourly">Stündlich</SelectItem>
                            <SelectItem value="daily">Täglich</SelectItem>
                            <SelectItem value="weekly">Wöchentlich</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-2">
                        <Label className="text-sm text-slate-600">Überwachen bis</Label>
                        <Input
                          type="date"
                          value={monitoringUntil}
                          onChange={(e) => setMonitoringUntil(e.target.value)}
                          min={new Date().toISOString().split('T')[0]}
                        />
                      </div>
                    </>
                  )}

                  <Button
                    onClick={handleSaveMonitoring}
                    disabled={isSavingMonitoring}
                    size="sm"
                    className="w-full"
                  >
                    {isSavingMonitoring ? (
                      <RefreshCw className="h-4 w-4 animate-spin mr-2" />
                    ) : (
                      <Check className="h-4 w-4 mr-2" />
                    )}
                    Einstellungen speichern
                  </Button>
                </div>
              </PopoverContent>
            </Popover>

            <Button
              variant="outline"
              size="sm"
              onClick={handleRefresh}
              disabled={isRefreshing || isBookingArchived}
              className="gap-1.5 md:gap-2 shrink-0"
              title={isBookingArchived ? 'Buchung wurde von Lufthansa archiviert' : undefined}
            >
              <RefreshCw className={cn('h-4 w-4', isRefreshing && 'animate-spin')} />
              <span className="hidden md:inline">Aktualisieren</span>
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content - Full Width Polenta Viewer */}
      <div className="flex-1 overflow-hidden flex flex-col">
        {/* Archived booking banner */}
        {isBookingArchived && (
          <div className="shrink-0 bg-slate-50 border-b border-slate-200 px-4 py-3">
            <div className="flex items-center gap-3 max-w-7xl mx-auto">
              <Archive className="h-5 w-5 text-slate-500" />
              <div>
                <p className="text-sm font-medium text-slate-700">
                  Diese Buchung wurde von Lufthansa archiviert
                </p>
                <p className="text-xs text-slate-500">
                  {booking.archived_at
                    ? `Archiviert am ${new Date(booking.archived_at).toLocaleDateString('de-DE', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}`
                    : 'Die Buchung ist nicht mehr im Lufthansa-System verfügbar.'}
                  {' '}Die zuletzt bekannten Daten werden angezeigt.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Content area */}
        <div className="flex-1 overflow-hidden">
          {versionData?.data && versionData?.dictionaries ? (
            <PolentaViewer
              bookingData={versionData.data}
              dictionaries={versionData.dictionaries}
              isLoading={isLoadingVersion}
            />
          ) : hasDataError ? (
            <div className="flex items-center justify-center h-full">
              <div className="flex flex-col items-center gap-4 max-w-md text-center px-4">
                <div className="h-16 w-16 rounded-full bg-slate-100 flex items-center justify-center">
                  <Archive className="h-8 w-8 text-slate-400" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900">
                  Keine Buchungsdaten verfügbar
                </h3>
                <p className="text-sm text-slate-500">
                  Die Buchung wurde archiviert, bevor Daten erfasst werden konnten.
                  Es sind keine historischen Daten für diese Buchung vorhanden.
                </p>
                {booking?.current_state?.errors?.[0] && (
                  <p className="text-xs text-slate-400 font-mono bg-slate-50 px-3 py-2 rounded">
                    {booking.current_state.errors[0].title}: {booking.current_state.errors[0].detail}
                  </p>
                )}
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center h-full">
              <div className="flex flex-col items-center gap-4">
                <div className="h-10 w-10 border-4 border-slate-200 border-t-blue-500 rounded-full animate-spin" />
                <p className="text-slate-500">
                  {isPolling ? 'Buchungsdaten werden von Lufthansa abgerufen...' : 'Buchungsdaten werden geladen...'}
                </p>
                {isPolling && (
                  <p className="text-xs text-slate-400">Dies kann bis zu einer Minute dauern</p>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Refresh Status Overlay */}
      <RefreshOverlay
        status={refreshStatus}
        onDismiss={() => setRefreshStatus('idle')}
      />
    </div>
  )
}
